import { faChevronDown, faWarning } from "@fortawesome/pro-regular-svg-icons";
import { twJoin, twMerge } from "tailwind-merge";

import { Pill } from "src/base-components/Pill";
import { DecisionTableOperators } from "src/clients/flow-api";
import { OPERATORS_WITH_IS_ANY } from "src/constants/operators";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { HIGHLIGHT_CLASSES } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { useHighlightedScrollIntoView } from "src/utils/useHighlightedScrollIntoview";

type Props = {
  value: string;
  variant: "predicate" | "effect";
  dataLoc?: string;
  error?: string;
  operator?: DecisionTableOperators;
  isHighlighted?: boolean;
  isSelected?: boolean;
  isSearchHighlighted?: boolean;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  readOnly: boolean;
  ref?: React.Ref<HTMLDivElement>;
};

export const DormantCodeInput: React.FC<Props> = ({
  value,
  variant,
  dataLoc,
  isHighlighted = false,
  isSelected = false,
  readOnly = false,
  operator,
  error = "",
  onClick,
  isSearchHighlighted,
  ref,
}) => {
  const [innerRef] = useHighlightedScrollIntoView(isSearchHighlighted);
  const borderClasses = (() => {
    if (isSelected) {
      return "border border-indigo-600";
    }

    if (isHighlighted) {
      return "border border-indigo-50";
    }

    return variant === "effect"
      ? "border border-green-50"
      : "border border-white focus-within:border-indigo-600";
  })();

  return (
    <div ref={ref} className="relative h-8 flex-grow overflow-hidden">
      <Tooltip disabled={!error} title={error} asChild>
        <div className="relative h-full w-full focus:outline-none focus:ring-0">
          <div
            className={twJoin(
              "item-center flex h-full w-full min-w-full",
              borderClasses,
            )}
          >
            <div className="min-w-0 grow">
              <div
                ref={innerRef}
                className={twMerge(
                  "flex items-center",
                  "h-full min-h-0 w-full min-w-0 max-w-full",
                  "pl-2",
                  "font-inter-normal-12px text-indigo-600",
                  variant === "effect" ? "bg-green-50" : "bg-white",
                  variant === "predicate" && !isHighlighted && "bg-white",
                  isHighlighted && "bg-indigo-50",
                  error && "border-red-600 ring-2 ring-red-400/25",
                  isSearchHighlighted && HIGHLIGHT_CLASSES,
                )}
                data-loc={dataLoc && `${dataLoc}-value`}
                onClick={onClick}
              >
                {operator && (
                  <div
                    className={twJoin(
                      "!h-full flex min-h-[30px] items-center self-start pt-0.5",
                      !readOnly && "cursor-pointer",
                    )}
                    onFocus={(e) => e.preventDefault()}
                  >
                    <div
                      className="dormant-operator-dropdown flex h-full w-full items-center justify-center rounded-lg border-l border-l-transparent p-0.5"
                      data-loc={dataLoc && `${dataLoc}-operator`}
                      onClick={onClick}
                    >
                      <Pill fontType="code" size="sm" variant="gray">
                        <Pill.Text>
                          {OPERATORS_WITH_IS_ANY[operator]?.code}
                        </Pill.Text>
                        <Pill.Icon icon={faChevronDown} />
                      </Pill>
                    </div>
                  </div>
                )}
                {error && (
                  <div className="absolute right-1">
                    <Icon variant="error" icon={faWarning} size="md" />
                  </div>
                )}
                <div
                  className={twJoin(
                    "flex items-center",
                    "pr-0.5 pl-1.5",
                    "h-full min-h-0 w-full min-w-0",
                    "font-code-12",
                  )}
                >
                  {/**
                   * Span needed because the parent div needs to be flex for layout, but text-ellipsis does not work with the flex there.
                   * This can be seen as the pendent to .cm-line
                   */}
                  <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                    {value}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Tooltip>
    </div>
  );
};
